import { ActionTree, ActionContext } from "vuex";
import { IAuthState } from "./types";
import { IRootState } from "@/store/types";
import AuthenticationContext, { UserInfo } from "adal-angular";
import jwt_decode from "jwt-decode";
import {
  AD_APP_CLIENTID,
  AD_APP_INSTANCE,
  AD_APP_TENANT,
  OLA_API_AD_APP_ID,
  LOYALTY_API_AD_APP_ID,
  API_BASE_URL,
  LOYALTY_BASE_URL,
  DECLINED_TRANSACTIONS_API_BASE_URL,
  DECLINED_TRANSACTIONS_APP_ID

} from "@/helpers/config";
import { AUTH_MUTATIONS } from "./mutations";

export namespace AUTH_ACTIONS {
  export const LOGIN: string = "login";
  export const LOGOUT: string = "logout";
  export const ACQUIRE_TOKEN: string = "acquireToken";
  export const ACQUIRE_LOYALTY_TOKEN: string = "acquireLoyaltyToken";
  export const ACQUIRE_DECLINED_TRANSACTIONS_TOKEN: string = "acquireDeclinedTransactionsToken";
  export const AUTHENTICATE: string = "authenticate";
  export const ASSUME_ROLE: string = "assumeRole";
  export const RESET_ROLES: string = "resetRoles";
}

const context: AuthenticationContext = new AuthenticationContext({
    instance: AD_APP_INSTANCE,
    tenant: AD_APP_TENANT,
    clientId: AD_APP_CLIENTID,
    redirectUri: `${window.location.origin}${process.env.BASE_URL}`,
    postLogoutRedirectUri: window.location.origin,
    cacheLocation: "localStorage",
    endpoints: {
      [API_BASE_URL]: OLA_API_AD_APP_ID,
      [LOYALTY_BASE_URL] : LOYALTY_API_AD_APP_ID,
      [DECLINED_TRANSACTIONS_API_BASE_URL] : DECLINED_TRANSACTIONS_APP_ID
    }
  });

// if the user was redirected here after logging in with Azure AD,
// let the adal context grab the token information from the url and setup the user.
if (context.isCallback(window.location.hash) || window.self !== window.top) {
  context.handleWindowCallback();
}

export const actions: ActionTree<IAuthState, IRootState> = {
  [AUTH_ACTIONS.LOGIN](): void {
    context.login();
  },
  [AUTH_ACTIONS.LOGOUT](): void {
    context.logOut();
  },
  [AUTH_ACTIONS.ACQUIRE_LOYALTY_TOKEN](
    { commit }: any,
    resource: string = LOYALTY_API_AD_APP_ID
  ): Promise<string> {
    // wrap the original method that takes a callback into a
    // promise to make it easier to consume.
    return new Promise((resolve, reject) => {
      context.acquireToken(
        resource,
        (err, token): void => {
          if (err) {
            // reject the Promise with an error.
            return reject(err);
          }

          // resolve/fulfill the promise with data.
          return resolve(token as string);
        }
      );
    });
  },
  [AUTH_ACTIONS.ACQUIRE_DECLINED_TRANSACTIONS_TOKEN](
    { commit }: any,
    resource: string = DECLINED_TRANSACTIONS_APP_ID
  ): Promise<string> {
    // wrap the original method that takes a callback into a
    // promise to make it easier to consume.
    return new Promise((resolve, reject) => {
      context.acquireToken(
        resource,
        (err, token): void => {
          if (err) {
            // reject the Promise with an error.
            return reject(err);
          }

          // resolve/fulfill the promise with data.
          return resolve(token as string);
        }
      );
    });
  },
  [AUTH_ACTIONS.ACQUIRE_TOKEN](
    { commit }: any,
    resource: string = OLA_API_AD_APP_ID
  ): Promise<string> {
    // wrap the original method that takes a callback into a
    // promise to make it easier to consume.
    return new Promise((resolve, reject) => {
      context.acquireToken(
        resource,
        (err, token): void => {
          if (err) {
            // reject the Promise with an error.
            return reject(err);
          }

          // resolve/fulfill the promise with data.
          return resolve(token as string);
        }
      );
    });
  },
  async [AUTH_ACTIONS.AUTHENTICATE]({
    dispatch,
    state,
    commit
  }: any): Promise<any> {
    // try pull the user out of local storage.
    const user: UserInfo = context.getCachedUser();

    if (user) {
      commit(AUTH_MUTATIONS.SET_USER, user);

      if (!state.roles) {
        // load the user roles for the user.
        // get them from the jwt access token since they aren't in the id token.
        const token: string = await dispatch(AUTH_ACTIONS.ACQUIRE_TOKEN);
        const userInfo: any = jwt_decode(token);
        commit(AUTH_MUTATIONS.SET_ROLES, userInfo.roles);
        commit(AUTH_MUTATIONS.SET_ISSUED_ROLES, userInfo.roles);
      }

      // this needs to be set after roles to prevent role auth firing before
      // the roles are set.
      commit(AUTH_MUTATIONS.SET_ISAUTHENTICATED, true);
      return;
    }

    // the user is not authenticated.
    commit(AUTH_MUTATIONS.SET_USER, undefined);
    commit(AUTH_MUTATIONS.SET_ROLES, undefined);
    commit(AUTH_MUTATIONS.SET_ISSUED_ROLES, undefined);
    commit(AUTH_MUTATIONS.SET_ISAUTHENTICATED, false);
    throw Error("User is not authenticated");
  },
  [AUTH_ACTIONS.ASSUME_ROLE](
    actionContext: ActionContext<IAuthState, IRootState>,
    role: string
  ): void {
    if (actionContext.state.issuedRoles.includes(role)) {
      actionContext.commit(AUTH_MUTATIONS.SET_ISASSUMINGROLE, true);
      actionContext.commit(AUTH_MUTATIONS.SET_ROLES, [role]);
    } else {
      throw Error("Cannot Assume role");
    }
  },
  [AUTH_ACTIONS.RESET_ROLES](
    actionContext: ActionContext<IAuthState, IRootState>
  ): void {
    actionContext.commit(AUTH_MUTATIONS.SET_ISASSUMINGROLE, false);
    actionContext.commit(AUTH_MUTATIONS.SET_ROLES, actionContext.state.issuedRoles);
  }
};
